import {
  FormattingToolbarController,
  SuggestionMenuController,
  SuggestionMenuProps
} from '@blocknote/react'
import { BlockNoteView, lightDefaultTheme } from '@blocknote/mantine'
import { CustomSuggestionMenu } from 'modules/Cube/view/layouts/quote/QuoteEditorContent/ContentEditor/CustomSuggestionMenu/CustomSuggestionMenu'
import { getCustomSuggestionMenuItems } from 'modules/Cube/view/layouts/quote/QuoteEditorContent/ContentEditor/CustomSuggestionMenu/menuItems'
import { useContentEditor } from 'modules/Cube/view/layouts/quote/QuoteEditorContent/ContentEditor/useContentEditor'
import '@blocknote/mantine/style.css'
import 'modules/Cube/view/layouts/quote/QuoteEditorContent/ContentEditor/BlockNoteOverrides.scss'
import { CustomFormattingToolbar } from 'modules/Cube/view/layouts/quote/QuoteEditorContent/ContentEditor/CustomFormattingToolbar/CustomFormattingToolbar'
import { QuoteVariablesSuggestionController } from './QuoteVariables'
import { SuggestionItem } from './CustomSuggestionMenu/types'
import { match } from 'ts-pattern'
import { usePermissions } from 'lib/rbac'
import { filterSuggestionItems } from '@blocknote/core'
import { blocknoteSchema } from './blocknoteSchema'
import { useMemo } from 'react'

// escape the overflow:auto/hidden ancestors in QuoteEditorLayout so menus/dropdowns
// (esp. the BlockTypeSelect dropdown, which renders inline via withinPortal={false})
// are not clipped near the viewport edge. `null` === document.body.
const PORTAL_ELEMENTS = { default: null }

const createMenu =
  (params: {
    editor: typeof blocknoteSchema.BlockNoteEditor
    addPhaseBlockEnabled: boolean
  }) =>
  (props: SuggestionMenuProps<SuggestionItem>) => {
    return (
      <CustomSuggestionMenu
        {...props}
        editor={params.editor}
        addPhaseBlockEnabled={params.addPhaseBlockEnabled}
        data-testid="blocks"
      />
    )
  }

export const ContentEditor = () => {
  const hook = useContentEditor()

  const { can } = usePermissions()

  const restrictedContentEditing = !can('org.quote:content.update')

  const Menu = useMemo(() => {
    return createMenu({
      editor: hook.features.contentEditor.editor,
      addPhaseBlockEnabled: hook.features.contentEditor.addPhaseBlock.enabled
    })
  }, [
    hook.features.contentEditor.editor,
    hook.features.contentEditor.addPhaseBlock.enabled
  ])

  return (
    <BlockNoteView
      data-testid="quote.contentEditor"
      editor={hook.features.contentEditor.editor}
      portalElements={PORTAL_ELEMENTS}
      theme={{ light: lightDefaultTheme, dark: lightDefaultTheme }}
      /**
       * We use a class here, and the BlockNoteOverrides.scss file, as we want to
       * be quite specific with out disabled state. We want to retain existing
       * formatting, such as colors, but apply a general opacity reduction (or
       * in the case of restricted sales users, only to non-phase blocks).
       */
      className={match({
        globallyDisabled: hook.features.contentEditor.disabled,
        restrictedForSales: restrictedContentEditing
      })
        .with({ globallyDisabled: true }, () => 'editing-disabled')
        .with(
          { globallyDisabled: false, restrictedForSales: true },
          () => 'content-editing-disabled'
        )
        .otherwise(() => undefined)}
      onChange={hook.features.contentEditor.onChange}
      slashMenu={false}
      formattingToolbar={false}
      editable={
        !hook.features.contentEditor.disabled && !restrictedContentEditing
      }
      onDragEnd={hook.features.contentEditor.onDragEnd}
    >
      <SuggestionMenuController
        triggerCharacter="/"
        getItems={query => {
          const editor = hook.features.contentEditor.editor
          const cursorBlockId = editor.getTextCursorPosition()?.block?.id
          const selectedBlock = cursorBlockId
            ? editor.getBlock(cursorBlockId)
            : null

          return Promise.resolve(
            filterSuggestionItems(
              getCustomSuggestionMenuItems(
                editor,
                hook.features.contentEditor.addPhaseBlock.enabled
              ).filter(item => {
                /**
                 * Within bullet/numbered lists we only want to offer those two
                 * options. Otherwise we have to deal with all kinds of
                 * shenanigans, like indentation and so on.
                 */
                if (
                  selectedBlock &&
                  ['numberedListItem', 'bulletListItem'].includes(
                    selectedBlock.type
                  )
                ) {
                  return ['Bullet list', 'Numbered list'].includes(item.title)
                }
                return true
              }),
              query
            )
          )
        }}
        suggestionMenuComponent={Menu}
      />
      <QuoteVariablesSuggestionController
        editor={hook.features.contentEditor.editor}
        addPhaseBlockEnabled={hook.features.contentEditor.addPhaseBlock.enabled}
        quoteVariablesValues={hook.features.quoteVariables.values}
      />
      <FormattingToolbarController
        formattingToolbar={CustomFormattingToolbar}
      />
    </BlockNoteView>
  )
}
